import React from 'react'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'

import {
  FormRender,
  type FieldSettings,
  type JSONContent,
} from '@cusman/cm-forms-library'
import { baseURL } from './baseUrl'
import {
  createSession,
  getSession,
  partialSubmit,
  submitSession,
  saveSessionToStorage,
  getSessionFromStorage,
  removeSessionFromStorage,
  FormData,
} from './api/session'
import { RestoreSessionDialog } from './RestoreSessionDialog'
// import { ThemeOptions } from '@mui/material'

// Строим EditorLayout из данных формы — общий маппинг для FormRender
// и диалога восстановления сессии
function buildLayoutFromForm(form: FormData | null) {
  return {
    showPagination: form?.settings?.layout?.show_pagination,
    contianerStyles: form?.settings.layout?.contianer_styles || '',
    bodyStyles: form?.settings.layout?.body_styles || '',
    fields: JSON.parse(form?.settings.layout?.fields || '{}') as Record<
      string,
      FieldSettings
    >,
    theme: form?.settings.layout?.theme,
  }
}

export default function App() {
  const [form, setForm] = React.useState<FormData | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<Error | null>(null)
  const [sessionId, setSessionId] = React.useState<string | null>(null)
  const [initialValues, setInitialValues] = React.useState<Record<
    string,
    any
  > | null>(null)
  // Данные сессии, ожидающие решения пользователя в диалоге восстановления
  const [showRestoreDialog, setShowRestoreDialog] = React.useState(false)
  const [pendingRestoreData, setPendingRestoreData] = React.useState<{
    form: FormData
    sessionId: string
    values: Record<string, any>
  } | null>(null)
  const formIdRef = React.useRef<string | null>(null)

  // Ref для debounce partial submit
  const partialSubmitTimeoutRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null)
  const lastPartialSubmitValuesRef = React.useRef<string>('')

  if (!formIdRef.current) {
    formIdRef.current = new URLSearchParams(window.location.search).get(
      'formId'
    )
  }

  // Загрузка формы и инициализация/восстановление сессии
  React.useEffect(() => {
    let isMounted = true
    const formId = formIdRef.current

    if (!formId) {
      setError(new Error('Form ID is missing in the URL query string'))
      setIsLoading(false)

      return () => {
        isMounted = false
      }
    }

    async function initializeFormAndSession() {
      let dialogShown = false

      try {
        // Проверяем, есть ли сохраненная сессия в localStorage
        const storedSession = getSessionFromStorage(formId!)

        if (storedSession) {
          // Пытаемся восстановить существующую сессию
          try {
            const sessionResponse = await getSession(
              formId!,
              storedSession.sessionId
            )

            if (isMounted) {
              if (sessionResponse.data.session.status === 'submitted') {
                // Сессия уже отправлена - создаем новую
                removeSessionFromStorage(formId!)
                await createNewSession(formId!, isMounted)
              } else {
                // Бэкенд отдает сохраненные значения в lastPartialSubmitValues,
                // session.values оставлен как fallback для старых версий API
                const savedValues =
                  sessionResponse.data.lastPartialSubmitValues ??
                  sessionResponse.data.session.values

                // Диалог показываем, только если есть хотя бы одно
                // непустое значение
                const hasSavedValues =
                  savedValues &&
                  typeof savedValues === 'object' &&
                  Object.values(savedValues).some((value) =>
                    Array.isArray(value)
                      ? value.length > 0
                      : value !== '' &&
                        value !== null &&
                        value !== undefined &&
                        value !== false
                  )

                if (hasSavedValues) {
                  // Есть сохраненные данные - спрашиваем пользователя,
                  // восстановить их или начать заново
                  setPendingRestoreData({
                    form: sessionResponse.data.form,
                    sessionId: storedSession.sessionId,
                    values: savedValues,
                  })
                  setShowRestoreDialog(true)
                  dialogShown = true
                } else {
                  // Сессия существует, но сохраненных данных нет -
                  // продолжаем без диалога
                  setForm(sessionResponse.data.form)
                  setSessionId(storedSession.sessionId)
                }
              }
            }
          } catch (sessionError) {
            // Сессия недоступна (устарела или удалена), создаем новую
            console.warn(
              'Failed to restore session, creating new one:',
              sessionError
            )
            removeSessionFromStorage(formId!)
            await createNewSession(formId!, isMounted)
          }
        } else {
          // Нет сохраненной сессии, создаем новую
          await createNewSession(formId!, isMounted)
        }
      } catch (err) {
        if (isMounted) {
          setError(err as Error)
        }
      } finally {
        // Если показан диалог восстановления, isLoading сбросят его обработчики
        if (isMounted && !dialogShown) {
          setIsLoading(false)
        }
      }
    }

    async function createNewSession(formId: string, isMounted: boolean) {
      // Создаем сессию - она возвращает и данные формы
      const sessionResponse = await createSession(formId)

      if (isMounted) {
        const sessionData = sessionResponse.data

        // Сохраняем sessionId в localStorage
        saveSessionToStorage(formId, sessionData.session.id)
        setSessionId(sessionData.session.id)

        // Используем данные формы из ответа createSession
        setForm(sessionData.form)
      }
    }

    initializeFormAndSession()

    return () => {
      isMounted = false
      // Очищаем debounce timeout при unmount
      if (partialSubmitTimeoutRef.current) {
        clearTimeout(partialSubmitTimeoutRef.current)
      }
    }
  }, [])

  // Пользователь согласился продолжить заполнение - восстанавливаем сессию
  const handleAcceptRestore = React.useCallback(() => {
    if (pendingRestoreData) {
      setForm(pendingRestoreData.form)
      setSessionId(pendingRestoreData.sessionId)
      setInitialValues(pendingRestoreData.values)
    }
    setShowRestoreDialog(false)
    setPendingRestoreData(null)
    setIsLoading(false)
  }, [pendingRestoreData])

  // Обработчик частичного сохранения (debounced)
  const handlePartialSubmit = React.useCallback(
    (values: Record<string, any>) => {
      const formId = formIdRef.current
      if (!formId || !sessionId) {
        return
      }

      // Проверяем, изменились ли значения
      const valuesString = JSON.stringify(values)
      if (valuesString === lastPartialSubmitValuesRef.current) {
        return
      }

      // Очищаем предыдущий timeout если есть
      if (partialSubmitTimeoutRef.current) {
        clearTimeout(partialSubmitTimeoutRef.current)
      }

      // Устанавливаем новый timeout для debounce (500ms)
      partialSubmitTimeoutRef.current = setTimeout(() => {
        lastPartialSubmitValuesRef.current = valuesString

        partialSubmit(formId, sessionId, values).catch((err) => {
          console.error('Failed to save partial data:', err)
        })
      }, 500)
    },
    [sessionId]
  )

  // Обработчик отказа от восстановления - создаем новую сессию
  const handleDeclineRestore = React.useCallback(async () => {
    setShowRestoreDialog(false)
    setPendingRestoreData(null)

    const formId = formIdRef.current
    if (formId) {
      try {
        removeSessionFromStorage(formId)
        const sessionResponse = await createSession(formId)
        const sessionData = sessionResponse.data

        saveSessionToStorage(formId, sessionData.session.id)
        setSessionId(sessionData.session.id)
        setForm(sessionData.form)
      } catch (err) {
        setError(err as Error)
      }
    }
    setIsLoading(false)
  }, [])

  // Обработчик финального submit
  const handleSubmit = React.useCallback(
    (values: unknown) => {
      const formId = formIdRef.current
      if (!formId) {
        return Promise.reject(
          new Error('Form ID is missing in the URL query string')
        )
      }

      // Если есть sessionId, используем новый endpoint
      if (sessionId) {
        return submitSession(formId, sessionId, values as Record<string, any>)
          .then((data) => {
            // Очищаем сессию из localStorage после успешного submit
            removeSessionFromStorage(formId)

            if (window.parent && window.parent !== window) {
              window.parent.postMessage(
                {
                  type: 'FORM_SUBMIT_SUCCESS',
                  formId: formId,
                  data: data,
                },
                '*'
              )
            }
          })
          .catch((submitError) => {
            console.error('Ошибка:', submitError)
            throw submitError
          })
      }

      // Fallback на старый endpoint если нет sessionId
      return fetch(`${baseURL}/form/${formId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          values,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (window.parent && window.parent !== window) {
            window.parent.postMessage(
              {
                type: 'FORM_SUBMIT_SUCCESS',
                formId: formId,
                data: data,
              },
              '*'
            )
          }
        })
        .catch((submitError) => {
          console.error('Ошибка:', submitError)
          throw submitError
        })
    },
    [sessionId]
  )

  const layout = React.useMemo(() => buildLayoutFromForm(form), [form])

  const content = React.useMemo(() => {
    return JSON.parse(form?.settings.content_json || '{}') as JSONContent
  }, [form])

  const validationScheme = React.useMemo(() => {
    return form?.validation_scheme // JSON.parse(form?.validation_scheme || '{}') as JSONContent
  }, [form])

  // Диалог восстановления показываем до загрузки формы (isLoading еще true)
  if (showRestoreDialog && pendingRestoreData) {
    return (
      <RestoreSessionDialog
        layout={buildLayoutFromForm(pendingRestoreData.form)}
        onContinue={handleAcceptRestore}
        onStartOver={handleDeclineRestore}
      />
    )
  }

  if (isLoading) return null
  if (error) return <div>{error.message}</div>
  if (!form) return null

  return (
    <FormRender
      content={content}
      layout={layout}
      validationScheme={validationScheme}
      onSubmit={handleSubmit}
      sessionId={sessionId}
      onPartialSubmit={handlePartialSubmit}
      initialValues={initialValues}
    />
  )
}
